import React, { useState, useMemo, useEffect, useRef } from 'react';
import Grid from '../components/Grid';
import { API_BASE_URL, AUTHORIZED_ROLES, plantNames } from '../constants/property';
import Pagination from '../components/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { Snackbar, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  parseRecordDateTime,
  compareRecordsNewestFirst,
  defaultSortDirectionForColumn,
} from '../utils/tableSort';

const ModalSection = ({ children }) => (
  <section className="mb-6 last:mb-0">
    {children}
  </section>
);

/** Returns field key → message for invalid modal fields; `{}` when valid. */
function validateAdvanceForm(formData, todayYmd) {
  const errors = {};
  if (!String(formData.supervisorName ?? '').trim()) {
    errors.supervisorName = 'Supervisor name is required.';
  }
  if (!String(formData.modeOfTranscation ?? '').trim()) {
    errors.modeOfTranscation = 'Mode of transaction is required.';
  }
  const dateStr = String(formData.dateOfTranscation ?? '').trim();
  if (!dateStr) {
    errors.dateOfTranscation = 'Date of transaction is required.';
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    errors.dateOfTranscation = 'Enter a valid date.';
  } else if (dateStr > todayYmd) {
    errors.dateOfTranscation = 'Date cannot be in the future.';
  }
  const amtRaw = String(formData.amount ?? '').trim();
  if (!amtRaw) {
    errors.amount = 'Amount is required.';
  } else {
    const n = parseFloat(amtRaw);
    if (!Number.isFinite(n) || n <= 0) {
      errors.amount = 'Enter a valid amount greater than 0.';
    }
  }
  if (!formData.fromBankAccount || !['TKD', 'DKD'].includes(formData.fromBankAccount)) {
    errors.fromBankAccount = 'Source is required.';
  }
  const m = formData.modeOfTranscation;
  if (m === 'Cheque' && !String(formData.transcationId ?? '').trim()) {
    errors.transcationId = 'Cheque number is required.';
  }
  return errors;
}

/** Only digits and at most one decimal point (for amount field). */
function sanitizeAmountInput(raw) {
  let out = '';
  let dotSeen = false;
  const s = String(raw ?? '');
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c >= '0' && c <= '9') out += c;
    else if (c === '.' && !dotSeen) {
      out += c;
      dotSeen = true;
    }
  }
  return out;
}

function firstAdvanceBankField(obj, keys) {
  if (!obj) return '';
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (!s || s === '-') continue;
    return s;
  }
  return '';
}

/**
 * Map API from-bank fields to controlled select values ("", "TKD", "DKD").
 * Backend often omits fromBankAccount, uses different casing, or only fills bankName.
 */
function normalizeFromBankForForm(fromBankAccountRaw, bankNameRaw) {
  const fromClean = String(fromBankAccountRaw ?? '').trim();
  const bankClean = String(bankNameRaw ?? '').trim();
  const fromLower = fromClean.toLowerCase();
  const bankLower = bankClean.toLowerCase();

  const isTkd =
    fromClean === 'TKD' ||
    fromLower === 'tkd' ||
    bankClean === 'TKD' ||
    bankLower === 'tkd';

  if (isTkd) {
    return { fromBankAccount: 'TKD', bankName: 'TKD' };
  }

  const isDkd =
    fromClean === 'DKD' ||
    fromLower === 'dkd' ||
    bankClean === 'DKD' ||
    bankLower === 'dkd';

  if (isDkd) {
    return { fromBankAccount: 'DKD', bankName: 'DKD' };
  }

  // Legacy (e.g. former "Others") or unknown: keep bank label for display; user must pick TKD or DKD
  if (fromClean || bankClean) {
    return { fromBankAccount: '', bankName: bankClean || fromClean };
  }

  return { fromBankAccount: '', bankName: '' };
}

function hasMeaningfulChequeNo(record) {
  if (!record) return false;
  const src = { ...record, ...(record.originalData || {}) };
  const ch = String(src.chequeNo ?? '').trim();
  if (ch && ch !== '-') return true;
  return false;
}

/**
 * Map API / legacy modal values to current select values (Cash, Cheque, UPI, onlineBank).
 * Legacy UI sent API "Cash" for cheque payments and "Cheque" for cash (labels were inverted).
 */
function normalizeModeForForm(mode, record) {
  const m = String(mode ?? '').trim();
  if (m === 'Bank') return 'UPI';
  if (m === 'Credit') return 'onlineBank';

  const hasCh = hasMeaningfulChequeNo(record);
  if (m === 'Cash') {
    return hasCh ? 'Cheque' : 'Cash';
  }
  if (m === 'Cheque') {
    return hasCh ? 'Cheque' : 'Cash';
  }
  return m;
}

const AdvanceDetails = () => {
  const { hasAnyRole } = useAuth();
  const isAdministrator = hasAnyRole(AUTHORIZED_ROLES);
  const columnMenuRef = useRef(null);
  const isTypingSupervisorRef = useRef(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [sortColumn, setSortColumn] = useState('dateOfTranscation');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [datePreset, setDatePreset] = useState(''); // today|yesterday|last7|last30|thisMonth|lastMonth|custom
  const [dateDisplay, setDateDisplay] = useState('');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showCustomCalendar, setShowCustomCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [selectedSupervisor, setSelectedSupervisor] = useState('all');
  const [supervisorInputValue, setSupervisorInputValue] = useState('');
  const [showSupervisorDropdown, setShowSupervisorDropdown] = useState(false);
  const [selectedModeOfTranscation, setSelectedModeOfTranscation] = useState('all');
  const [selectedBankName, setSelectedBankName] = useState('all');
  const defaultVisibleColumns = {
    dateOfTranscation: true,
    siteCode: true,
    plant: true,
    supervisorName: true,
    modeOfTranscation: true,
    transcationId: true,
    chequeNo: true,
    transcationdetails: true,
    amount: true,
    bankName: true,
    comments: true,
    actions: true
  };
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [formData, setFormData] = useState({
    supervisorName: '',
    siteCode: '',
    plant: '',
    modeOfTranscation: '',
    transcationId: '',
    chequeNo: '',
    transcationdetails: '',
    comments: '',
    dateOfTranscation: getTodayDate(),
    amount: '',
    fromBankAccount: '',
    bankName: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [supervisorsFromApi, setSupervisorsFromApi] = useState([]);
  const [maintenanceSiteOptions, setMaintenanceSiteOptions] = useState(() => [
    { value: 'ST1998', label: 'ST1998' },
    { value: 'ST2765', label: 'ST2765' },
    { value: 'ST3865', label: 'ST3865' },
    { value: 'ST4765', label: 'ST4765' },
    { value: 'ST19098', label: 'ST19098' },
    { value: 'ST2003', label: 'ST2003' },
  ]);
  const siteOptions = maintenanceSiteOptions;

  const [maintenancePlantOptions, setMaintenancePlantOptions] = useState(() => {
    const src = plantNames?.length ? plantNames : [{ name: 'Plant', value: 'Plant', label: 'Plant' }];
    return src.map((p) => ({
      value: String(p.value ?? p.name ?? '').trim(),
      label: String(p.label ?? p.name ?? p.value ?? '').trim() || String(p.value ?? p.name ?? ''),
    })).filter((o) => o.value);
  });
  const plantOptions = maintenancePlantOptions;

  const [advanceDetails, setAdvanceDetails] = useState([]);

  // Fetch advance details from API
  const fetchAdvanceDetails = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/supervisor/advanceDetails`, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Advance Details API Response:', data);
      
      // Transform API data to match table structure
      const transformedData = Array.isArray(data) ? data.map((item, index) => ({
        id: item.advanceId,
        apiIndex: index,
        rawDate: item.dateOfTranscation || '',
        advanceId: item.advanceId,
        siteCode: item.siteCode || item.sitecode || '-',
        plant: item.plant || item.fromPlant || item.fromPlants || item.plantName || '-',
        supervisorName: item.supervisorName || '-',
        modeOfTranscation: item.modeOfTranscation || '-',
        transcationId: item.transcationId || '-',
        chequeNo: item.chequeNo || '-',
        transcationdetails: item.transcationdetails || '-',
        comments: item.comments || '-',
        dateOfTranscation: item.dateOfTranscation || '-',
        amount: item.amount || '0',
        fromBankAccount:
          firstAdvanceBankField(item, [
            'fromBankAccount',
            'FromBankAccount',
            'fromBank',
            'from_bank_account',
          ]) || '',
        bankName:
          firstAdvanceBankField(item, ['bankName', 'BankName', 'bank_name']) || '',
        // Keep original data for reference
        originalData: item
      })) : [];

      transformedData.sort(compareRecordsNewestFirst);
      setAdvanceDetails(transformedData);
      
    } catch (err) {
      console.error('Error fetching advance details:', err);
      setError(err.message || 'Failed to fetch advance details');
      setAdvanceDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load advance details on component mount
  useEffect(() => {
    fetchAdvanceDetails();
  }, []);

  // Fetch supervisors from employee API (backend may use "Supervisior" — see Transport.js / property roles)
  useEffect(() => {
    const fetchSupervisors = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/employee`, {
          method: 'GET',
          mode: 'cors'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        const options = (Array.isArray(data) ? data : [])
          .filter((emp) => {
            const r = (emp.role || '').trim();
            return r === 'Supervisior' || r === 'Supervisor';
          })
          .map(emp => {
            const label =
              emp.userName ||
              emp.employeeName ||
              `${emp.firstName || ''} ${emp.lastName || ''}`.trim();
            return label ? { value: label, label } : null;
          })
          .filter(Boolean);

        setSupervisorsFromApi(options);
      } catch (err) {
        console.error('Error fetching supervisors from employee API:', err);
      }
    };

    fetchSupervisors();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Site`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'site');
        const seen = new Set();
        const opts = filtered
          .map((r) => {
            const label = String(r.value ?? r.code ?? '').trim();
            if (!label) return null;
            const key = label.toLowerCase().replace(/\s+/g, '');
            if (seen.has(key)) return null;
            seen.add(key);
            return { value: label, label };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        if (!cancelled && opts.length) setMaintenanceSiteOptions(opts);
      } catch {
        /* keep seeded site list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/maintence/Plant`, { method: 'GET', mode: 'cors' });
        if (!res.ok) return;
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === 'plant');
        const seen = new Set();
        const opts = filtered
          .map((r) => {
            const label = String(r.value ?? r.code ?? '').trim();
            if (!label) return null;
            const key = label.toLowerCase().replace(/\s+/g, '');
            if (seen.has(key)) return null;
            seen.add(key);
            return { value: label, label };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        if (!cancelled && opts.length) setMaintenancePlantOptions(opts);
      } catch {
        /* keep seeded plant list */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showColumnMenu && columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
        setShowColumnMenu(false);
      }
      if (showSupervisorDropdown && !event.target.closest('.supervisor-autocomplete-container')) {
        setShowSupervisorDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnMenu, showSupervisorDropdown]);

  const formatDateMMMDDYYYY = (dateValue) => {
    if (!dateValue || dateValue === '-') return '-';
    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return dateValue;
    return parsedDate.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };

  // Date preset display and handling (same as Vendors.js)
  useEffect(() => {
    if (!datePreset) {
      setDateDisplay('');
      return;
    }

    const now = new Date();
    const toDisplay = (d) =>
      d.toLocaleDateString('en-US', {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
    const clone = (d) => new Date(d.getTime());
    let start = clone(now);
    let end = clone(now);

    switch (datePreset) {
      case 'today':
        break;
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;
      case 'last7':
        start.setDate(start.getDate() - 6);
        break;
      case 'last30':
        start.setDate(start.getDate() - 29);
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'lastMonth': {
        const firstLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        start = firstLastMonth;
        end = lastLastMonth;
        break;
      }
      case 'custom':
        if (!dateDisplay) {
          setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
        }
        return;
      default:
        break;
    }
    setDateDisplay(`${toDisplay(start)} - ${toDisplay(end)}`);
  }, [datePreset]);

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    setShowDateDropdown(false);
    if (preset === 'custom') {
      setShowCustomCalendar(true);
    } else {
      setShowCustomCalendar(false);
    }
  };

  // Calendar helper functions (same as Vendors.js)
  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
  };
  const isSameDate = (date1, date2) => {
    if (!date1 || !date2) return false;
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    );
  };
  const navigateMonth = (direction) => {
    setCurrentMonth((prev) => {
      const newMonth = new Date(prev);
      newMonth.setMonth(prev.getMonth() + direction);
      return newMonth;
    });
  };
  const handleDateClick = (date) => {
    if (!customStartDate || (customStartDate && customEndDate)) {
      setCustomStartDate(date);
      setCustomEndDate(null);
    } else if (customStartDate && !customEndDate) {
      if (date < customStartDate) {
        setCustomEndDate(customStartDate);
        setCustomStartDate(date);
      } else {
        setCustomEndDate(date);
      }
    }
  };
  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setDateDisplay(`${formatDate(customStartDate)} - ${formatDate(customEndDate)}`);
      setShowCustomCalendar(false);
      setShowDateDropdown(false);
    }
  };
  const handleCancelCustomRange = () => {
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setCustomStartDate(null);
    setCustomEndDate(null);
  };
  const handleClearDate = (e) => {
    e.stopPropagation();
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
  };

  const parseDateValue = (dateValue) => {
    if (!dateValue || dateValue === '-') return new Date(0);
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [year, month, day] = dateValue.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    const parsed = new Date(dateValue);
    return Number.isNaN(parsed.getTime())
      ? new Date(0)
      : new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate || !date) return false;
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const s = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const e = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return d >= s && d <= e;
  };

  const modeOfTranscationOptions = useMemo(() => {
    const values = advanceDetails
      .map((x) => (x.modeOfTranscation || '').toString().trim())
      .filter(Boolean);
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [advanceDetails]);

  const bankNameOptions = useMemo(() => {
    const values = advanceDetails
      .map((x) => (x.bankName || '').toString().trim())
      .filter(Boolean);
    return Array.from(new Set(values)).sort((a, b) => a.localeCompare(b));
  }, [advanceDetails]);

  const supervisorFilterOptions = useMemo(() => {
    if (supervisorsFromApi && supervisorsFromApi.length > 0) {
      return [{ value: 'all', label: 'All' }, ...supervisorsFromApi];
    }
    const supervisors = [
      ...new Set(
        advanceDetails
          .map((item) => (item.supervisorName || '').toString().trim())
          .filter((s) => s && s !== '-')
      ),
    ].sort();
    return [{ value: 'all', label: 'All' }, ...supervisors.map((s) => ({ value: s, label: s }))];
  }, [advanceDetails, supervisorsFromApi]);

  useEffect(() => {
    if (!isTypingSupervisorRef.current) {
      if (selectedSupervisor === 'all') {
        setSupervisorInputValue('');
      } else {
        const selectedOption = supervisorFilterOptions.find((opt) => opt.value === selectedSupervisor);
        if (selectedOption) {
          setSupervisorInputValue(selectedOption.label);
        } else {
          setSupervisorInputValue('');
        }
      }
    }
  }, [selectedSupervisor, supervisorFilterOptions]);

  const filteredSupervisorOptions = useMemo(() => {
    if (!supervisorInputValue.trim()) {
      return supervisorFilterOptions;
    }
    const searchTerm = supervisorInputValue.toLowerCase();
    return supervisorFilterOptions.filter((opt) => opt.label.toLowerCase().includes(searchTerm));
  }, [supervisorFilterOptions, supervisorInputValue]);

  const handleSupervisorInputChange = (e) => {
    const value = e.target.value;
    isTypingSupervisorRef.current = true;
    setSupervisorInputValue(value);
    setShowSupervisorDropdown(true);

    if (!value.trim()) {
      setSelectedSupervisor('all');
    }

    setTimeout(() => {
      isTypingSupervisorRef.current = false;
    }, 100);
  };

  const handleSupervisorSelect = (option) => {
    isTypingSupervisorRef.current = false;
    setSelectedSupervisor(option.value);
    setSupervisorInputValue(option.label);
    setShowSupervisorDropdown(false);
    setCurrentPage(1);
  };

  const handleClearSupervisor = (e) => {
    e.stopPropagation();
    setSelectedSupervisor('all');
    setSupervisorInputValue('');
    setShowSupervisorDropdown(false);
    setCurrentPage(1);
  };

  const hasActiveFilters = useMemo(() => {
    return (
      datePreset !== '' ||
      dateDisplay !== '' ||
      selectedSupervisor !== 'all' ||
      selectedModeOfTranscation !== 'all' ||
      selectedBankName !== 'all'
    );
  }, [datePreset, dateDisplay, selectedSupervisor, selectedModeOfTranscation, selectedBankName]);

  useEffect(() => {
    setCurrentPage(1);
  }, [datePreset, dateDisplay, selectedSupervisor, selectedModeOfTranscation, selectedBankName]);

  const handleResetFilters = () => {
    setDatePreset('');
    setDateDisplay('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setShowCustomCalendar(false);
    setShowDateDropdown(false);
    setSelectedSupervisor('all');
    setSupervisorInputValue('');
    setShowSupervisorDropdown(false);
    setSelectedModeOfTranscation('all');
    setSelectedBankName('all');
    setCurrentPage(1);
  };

  // Compute filtered and sorted data
  const filteredData = useMemo(() => {
    let startDate = null;
    let endDate = null;
    const today = new Date();

    if (datePreset && datePreset !== 'custom') {
      switch (datePreset) {
        case 'today': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = d;
          endDate = d;
          break;
        }
        case 'yesterday': {
          const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
          startDate = d;
          endDate = d;
          break;
        }
        case 'last7': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
          break;
        }
        case 'last30': {
          endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
          startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
          break;
        }
        case 'thisMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth(), 1);
          endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          break;
        }
        case 'lastMonth': {
          startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          endDate = new Date(today.getFullYear(), today.getMonth(), 0);
          break;
        }
        default:
          break;
      }
    } else if (datePreset === 'custom' && customStartDate && customEndDate) {
      startDate = customStartDate;
      endDate = customEndDate;
    }

    let filtered = [...advanceDetails].filter((item) => {
      const supervisorOk =
        selectedSupervisor === 'all' ||
        (item.supervisorName || '').toString().trim().toLowerCase() ===
          selectedSupervisor.toString().trim().toLowerCase();

      const modeOk =
        selectedModeOfTranscation === 'all' ||
        (item.modeOfTranscation || '').toString().toLowerCase() === selectedModeOfTranscation.toString().toLowerCase();

      const bankOk =
        selectedBankName === 'all' ||
        (item.bankName || '').toString().toLowerCase() === selectedBankName.toString().toLowerCase();

      let dateOk = true;
      if (startDate && endDate) {
        const itemDate = parseDateValue(item.dateOfTranscation);
        dateOk = isDateInRange(itemDate, startDate, endDate);
      }

      return supervisorOk && modeOk && bankOk && dateOk;
    });

    // Apply sorting
    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn];
        let bValue = b[sortColumn];

        // Handle different data types
        if (sortColumn === 'dateOfTranscation') {
          aValue = parseRecordDateTime(a.rawDate || a.dateOfTranscation);
          bValue = parseRecordDateTime(b.rawDate || b.dateOfTranscation);
        } else if (sortColumn === 'amount') {
          // Handle numeric values
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          // Handle string values
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return compareRecordsNewestFirst(a, b);
      });
    }

    return filtered;
  }, [advanceDetails, selectedSupervisor, selectedModeOfTranscation, selectedBankName, datePreset, customStartDate, customEndDate, sortColumn, sortDirection]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const advanceExportHeaders = [
    'Advance ID',
    'Date of Transaction',
    'Site Code',
    'Plant',
    'Mode of Transaction',
    'Cheque No',
    'Transaction ID',
    'Transaction Details',
    'Supervisor Name',
    'Amount',
    'Bank',
    'Comments',
  ];

  const mapAdvanceRowForExport = (item) => [
    item.advanceId ?? item.id ?? '',
    formatDateMMMDDYYYY(item.dateOfTranscation),
    item.siteCode ?? '',
    item.plant ?? '',
    item.modeOfTranscation ?? '',
    item.chequeNo ?? '',
    item.transcationId ?? '',
    item.transcationdetails ?? '',
    item.supervisorName ?? '',
    item.amount ?? '',
    item.bankName ?? '',
    item.comments ?? '',
  ];

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Advance Details Report', 14, 15);
    if (dateDisplay) {
      doc.setFontSize(10);
      doc.text(`Date Range: ${dateDisplay}`, 14, 22);
    }
    const tableData = filteredData.map((item) => mapAdvanceRowForExport(item));
    autoTable(doc, {
      startY: dateDisplay ? 28 : 22,
      head: [advanceExportHeaders],
      body: tableData,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [0, 150, 136], textColor: 255 },
      margin: { top: 20 },
    });
    doc.save(`Advance_Details_Report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleExportExcel = () => {
    const rows = filteredData.map((item) => mapAdvanceRowForExport(item));
    const worksheet = XLSX.utils.aoa_to_sheet([advanceExportHeaders, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Advance Details');
    worksheet['!cols'] = [
      { wch: 10 },
      { wch: 14 },
      { wch: 12 },
      { wch: 14 },
      { wch: 16 },
      { wch: 12 },
      { wch: 14 },
      { wch: 28 },
      { wch: 18 },
      { wch: 12 },
      { wch: 18 },
      { wch: 24 },
    ];
    XLSX.writeFile(workbook, `Advance_Details_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection(defaultSortDirectionForColumn(column));
    }
    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <svg className="w-4 h-4 text-white opacity-70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    if (sortDirection === 'asc') {
      return (
        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      );
    }
    return (
      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const visibleCount = Object.values(prev).filter(v => v).length;
      if (prev[columnKey] && visibleCount === 1) {
        return prev;
      }
      return {
        ...prev,
        [columnKey]: !prev[columnKey]
      };
    });
  };
  const handleResetColumns = () => {
    setVisibleColumns(defaultVisibleColumns);
  };

  const columnDefinitions = [
    { key: 'dateOfTranscation', label: 'Date of Transaction' },
    { key: 'siteCode', label: 'Site Code' },
    { key: 'plant', label: 'Plant' },
    { key: 'supervisorName', label: 'Supervisor Name' },
    { key: 'modeOfTranscation', label: 'Mode of Transaction' },
    { key: 'transcationId', label: 'Transaction ID' },
    { key: 'chequeNo', label: 'Cheque No' },
    { key: 'transcationdetails', label: 'Transaction Details' },
    { key: 'amount', label: 'Amount' },
    { key: 'bankName', label: 'Bank' },
    { key: 'comments', label: 'Comments' },
    { key: 'actions', label: 'Actions' }
  ];

  const getVisibleColumnCount = () => {
    return Object.values(visibleColumns).filter(v => v).length;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'amount' ? sanitizeAmountInput(value) : value;
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: nextValue
      };
      // When Source is TKD or DKD, align bankName for API
      if (name === 'fromBankAccount') {
        if (nextValue === 'TKD') {
          newData.bankName = 'TKD';
        } else if (nextValue === 'DKD') {
          newData.bankName = 'DKD';
        } else {
          newData.bankName = '';
        }
        setFormErrors((p) => {
          const next = { ...p };
          delete next.bankName;
          return next;
        });
      }
      if (name === 'modeOfTranscation') {
        setFormErrors((p) => {
          const next = { ...p };
          delete next.transcationId;
          return next;
        });
      }
      return newData;
    });
  };

  const modalFieldClass = (fieldName, base = '') => {
    const err = formErrors[fieldName];
    return `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
      err ? 'border-red-500' : 'border-gray-300'
    }${base ? ` ${base}` : ''}`;
  };

  const plantSelectOptions = useMemo(() => {
    const code = String(formData.plant ?? '').trim();
    if (!code || plantOptions.some((o) => o.value === code)) return plantOptions;
    return [...plantOptions, { value: code, label: code }];
  }, [plantOptions, formData.plant]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fieldErrors = validateAdvanceForm(formData, getTodayDate());
    setFormErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) {
      return;
    }

    // Convert date to ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
    const convertDateToISO = (dateStr) => {
      if (!dateStr) return new Date().toISOString();
      // If already in YYYY-MM-DD format, convert to ISO
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const date = new Date(dateStr);
        return date.toISOString();
      }
      // If in other format, try to parse
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
    };

    // When mode is Cheque, the reference field maps to chequeNo; otherwise to transcationId
    const chequeNoValue = formData.modeOfTranscation === 'Cheque'
      ? (formData.transcationId || formData.chequeNo || '')
      : (formData.chequeNo || '');
    const transcationIdValue = formData.modeOfTranscation === 'Cheque'
      ? ''
      : (formData.transcationId || '');

    try {
      if (isEditMode) {
        // Update existing record - using PUT method
        const record = advanceDetails.find(item => item.id === editingId);
        if (!record || !record.advanceId) {
          setSuccessMessage('Advance ID not found. Close the dialog and try again.');
          setSnackbarSeverity('warning');
          setSnackbarOpen(true);
          return;
        }

        const updatePayload = {
          advanceId: record.advanceId,
          supervisorName: formData.supervisorName || '',
          siteCode: formData.siteCode || '',
          plant: formData.plant || '',
          modeOfTranscation: formData.modeOfTranscation || '',
          transcationId: transcationIdValue,
          transcationdetails: formData.transcationdetails || '',
          comments: formData.comments || '',
          dateOfTranscation: convertDateToISO(formData.dateOfTranscation),
          amount: parseFloat(formData.amount) || 0,
          chequeNo: chequeNoValue,
          fromBankAccount: formData.fromBankAccount || '',
          bankName:
            formData.fromBankAccount === 'TKD'
              ? 'TKD'
              : formData.fromBankAccount === 'DKD'
                ? 'DKD'
                : ''
        };

        const response = await fetch(`${API_BASE_URL}/api/supervisor/advanceDetails`, {
          method: 'PUT',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatePayload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log('Advance detail updated successfully:', responseData);

        // Refresh data after update
        await fetchAdvanceDetails();
        setCurrentPage(1);
        
        setSuccessMessage('Advance detail updated successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
      } else {
        // Create new record - using POST method
        const createPayload = {
          advanceId: 0,
          supervisorName: formData.supervisorName || '',
          siteCode: formData.siteCode || '',
          plant: formData.plant || '',
          modeOfTranscation: formData.modeOfTranscation || '',
          transcationId: transcationIdValue,
          transcationdetails: formData.transcationdetails || '',
          comments: formData.comments || '',
          dateOfTranscation: convertDateToISO(formData.dateOfTranscation),
          amount: parseFloat(formData.amount) || 0,
          chequeNo: chequeNoValue,
          fromBankAccount: formData.fromBankAccount || '',
          bankName:
            formData.fromBankAccount === 'TKD'
              ? 'TKD'
              : formData.fromBankAccount === 'DKD'
                ? 'DKD'
                : ''
        };

        const response = await fetch(`${API_BASE_URL}/api/supervisor/advanceDetails`, {
          method: 'POST',
          mode: 'cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(createPayload)
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
        }

        const responseData = await response.json();
        console.log('Advance detail created successfully:', responseData);

        // Refresh data after create
        await fetchAdvanceDetails();
        setCurrentPage(1);
        
        setSuccessMessage('Advance detail added successfully!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleClose();
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} advance detail:`, error);
      setSuccessMessage(`Failed to ${isEditMode ? 'update' : 'add'} advance detail: ${error.message}`);
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormErrors({});
    setFormData({
      supervisorName: '',
      siteCode: '',
      plant: '',
      modeOfTranscation: '',
      transcationId: '',
      chequeNo: '',
      transcationdetails: '',
      comments: '',
      dateOfTranscation: getTodayDate(),
      amount: '',
      fromBankAccount: '',
      bankName: ''
    });
  };

  const handleEdit = (id) => {
    const record = advanceDetails.find(item => item.id === id);
    if (record) {
      // Convert date from ISO format to YYYY-MM-DD format for input
      const convertDateToInputFormat = (dateStr) => {
        if (!dateStr || dateStr === '-') return getTodayDate();
        // Check if already in YYYY-MM-DD format
        if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateStr;
        }
        // Parse ISO format (YYYY-MM-DDTHH:mm:ss.sssZ) or other formats
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return getTodayDate();
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        } catch (e) {
          return getTodayDate();
        }
      };

      const pickAdvanceRefField = (chequeVal, txVal) => {
        const c = String(chequeVal ?? '').trim();
        const t = String(txVal ?? '').trim();
        if (c && c !== '-') return c;
        if (t && t !== '-') return t;
        return '';
      };

      const normMode = normalizeModeForForm(record.modeOfTranscation || '', record);
      const orig = record.originalData || {};

      let transactionIdValue = '';
      if (normMode === 'Cheque') {
        transactionIdValue = pickAdvanceRefField(record.chequeNo, record.transcationId);
        if (!transactionIdValue) {
          transactionIdValue = pickAdvanceRefField(orig.chequeNo, orig.transcationId);
        }
      } else if (
        normMode === 'UPI' ||
        normMode === 'onlineBank' ||
        normMode === 'Bank' ||
        normMode === 'Credit'
      ) {
        transactionIdValue = pickAdvanceRefField(null, record.transcationId);
        if (!transactionIdValue) {
          transactionIdValue = pickAdvanceRefField(null, orig.transcationId);
        }
      }

      // Prefer original API object so empty transformed fields don't hide saved values
      const src = { ...record, ...orig };
      const rawFrom = firstAdvanceBankField(src, [
        'fromBankAccount',
        'FromBankAccount',
        'fromBank',
        'from_bank_account',
      ]);
      const rawBank = firstAdvanceBankField(src, ['bankName', 'BankName', 'bank_name']);
      const { fromBankAccount, bankName } = normalizeFromBankForForm(rawFrom, rawBank);

      setFormData({
        supervisorName: record.supervisorName || '',
        siteCode: record.siteCode || record.sitecode || '',
        plant: record.plant && record.plant !== '-' ? record.plant : '',
        modeOfTranscation: normMode,
        transcationId: transactionIdValue,
        chequeNo: record.chequeNo === '-' ? '' : record.chequeNo || '',
        transcationdetails: record.transcationdetails === '-' ? '' : (record.transcationdetails || ''),
        comments: record.comments === '-' ? '' : (record.comments || ''),
        dateOfTranscation: convertDateToInputFormat(record.dateOfTranscation),
        amount: record.amount || '',
        fromBankAccount,
        bankName,
      });
      setFormErrors({});
      setIsEditMode(true);
      setEditingId(id);
      setIsModalOpen(true);
    }
  };

  const handleDelete = (id) => {
    setRecordToDelete(id);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/supervisor/${recordToDelete}`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Refresh the list after deletion
      await fetchAdvanceDetails();
      setSuccessMessage('Advance detail deleted successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    } catch (err) {
      console.error('Error deleting advance detail:', err);
      setError(err.message || 'Failed to delete advance detail');
      setSuccessMessage('Failed to delete advance detail');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      setShowDeleteConfirm(false);
      setRecordToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setRecordToDelete(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    setSnackbarOpen(false);
    setSuccessMessage('');
  };

  const handleAddNew = () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      supervisorName: '',
      siteCode: '',
      plant: '',
      modeOfTranscation: '',
      transcationId: '',
      chequeNo: '',
      transcationdetails: '',
      comments: '',
      dateOfTranscation: getTodayDate(),
      amount: '',
      fromBankAccount: '',
      bankName: ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  return (
    <>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        style={{position: 'absolute', bottom: '10px', left: '10px'}}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          sx={{ 
            width: '100%', 
            backgroundColor: snackbarSeverity === 'success' ? '#43a047' : '#dc2626', 
            color: '#fff', 
            fontWeight: '400',
            fontSize: '18px' 
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      {error && (
        <div className="bg-red-200 border border-red-400 text-red-700 px-4 py-3 rounded" style={{position: 'absolute', bottom: '10px', left: '10px', zIndex: '1000', minWidth: '200px'}}>
          {error}
        </div>
      )}
      
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Advance Details</h1>
        <div className="flex items-center space-x-3">
          {/* Column Visibility Toggle */}
          <div ref={columnMenuRef} className="relative column-menu-container">
            <button 
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Toggle Columns"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              
            </button>
            {showColumnMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-96 overflow-y-auto">
                <div className="py-1">
                  <div className="px-3 py-2 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={handleResetColumns}
                      className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                  {columnDefinitions.map(col => (
                    <label key={col.key} className="flex items-center px-4 py-2 hover:bg-gray-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                        disabled={visibleColumns[col.key] && getVisibleColumnCount() === 1}
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700">{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          {isAdministrator && (
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title={showFilters ? 'Hide Filters' : 'Show Filters'}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z"
                />
              </svg>
              <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
            </button>
          )}
          <button
            onClick={handleAddNew}
            className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            style={{ backgroundColor: '#009688' }}
          >
            Add Advance Detail
          </button>
        </div>
      </div>

      {/* Filters — same as Vendors: only for AUTHORIZED_ROLES (property / login) */}
      {isAdministrator && showFilters && (
        <div className="p-4 flex flex-wrap items-end gap-4 bg-white shadow rounded-lg" style={{ marginTop: '10px' }}>
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">
              {datePreset === '' ? 'Choose Date' : 'Selected Date Range: '}
              {datePreset && (
                <span className="font-bold text-black text-xs uppercase">
                  (
                  {datePreset === 'today'
                    ? 'Today'
                    : datePreset === 'yesterday'
                    ? 'Yesterday'
                    : datePreset === 'last7'
                    ? 'Last 7 Days'
                    : datePreset === 'last30'
                    ? 'Last 30 Days'
                    : datePreset === 'thisMonth'
                    ? 'This Month'
                    : datePreset === 'lastMonth'
                    ? 'Last Month'
                    : datePreset === 'custom'
                    ? 'Custom Range'
                    : datePreset}
                  )
                </span>
              )}
            </label>
            <div className="relative date-dropdown-container">
              <div className="relative">
                <input
                  type="text"
                  value={dateDisplay || 'Select Range'}
                  readOnly
                  onClick={() => setShowDateDropdown(!showDateDropdown)}
                  className="w-full px-3 py-2 pl-10 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Select Date Range"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                {dateDisplay && (
                  <button
                    type="button"
                    onClick={handleClearDate}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:bg-gray-100 rounded-r-md"
                    title="Clear date"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {showDateDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg z-20">
                  <div className="py-1">
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('today')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Today
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('yesterday')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Yesterday
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('last7')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Last 7 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('last30')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Last 30 Days
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('thisMonth')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      This Month
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('lastMonth')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Last Month
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDatePresetChange('custom')}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 ${
                        datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700'
                      }`}
                    >
                      Custom Range
                    </button>
                  </div>
                </div>
              )}

              {showCustomCalendar && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-30 p-4">
                  <div className="flex">
                    <div className="w-48 pr-4 border-r border-gray-200">
                      <div className="space-y-1">
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('today')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'today' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Today
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('yesterday')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'yesterday' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Yesterday
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('last7')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'last7' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Last 7 Days
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('last30')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'last30' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Last 30 Days
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('thisMonth')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'thisMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          This Month
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('lastMonth')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'lastMonth' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Last Month
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDatePresetChange('custom')}
                          className={`w-full text-left px-3 py-2 text-sm rounded ${
                            datePreset === 'custom' ? 'bg-orange-100 text-orange-600' : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          Custom Range
                        </button>
                      </div>
                    </div>

                    <div className="pl-4">
                      <div className="flex items-center justify-between mb-4">
                        <button type="button" onClick={() => navigateMonth(-1)} className="p-1 hover:bg-gray-100 rounded">
                          <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <div className="text-sm font-medium text-gray-900">
                          {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </div>
                        <button type="button" onClick={() => navigateMonth(1)} className="p-1 hover:bg-gray-100 rounded">
                          <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>

                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                          <div key={d} className="text-xs text-gray-500 text-center py-1">
                            {d}
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-7 gap-1">
                        {(() => {
                          const daysInMonth = getDaysInMonth(currentMonth);
                          const firstDay = getFirstDayOfMonth(currentMonth);
                          const days = [];

                          const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 0);
                          for (let i = firstDay - 1; i >= 0; i--) {
                            const day = prevMonth.getDate() - i;
                            days.push(
                              <button
                                key={`prev-${day}`}
                                type="button"
                                className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                disabled
                              >
                                {day}
                              </button>
                            );
                          }

                          for (let day = 1; day <= daysInMonth; day++) {
                            const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
                            const isSelected = isSameDate(date, customStartDate) || isSameDate(date, customEndDate);
                            const isInRange =
                              customStartDate && customEndDate && isDateInRange(date, customStartDate, customEndDate);

                            days.push(
                              <button
                                key={day}
                                type="button"
                                onClick={() => handleDateClick(date)}
                                className={`text-xs py-1 rounded hover:bg-gray-100 ${
                                  isSelected
                                    ? 'bg-orange-500 text-white'
                                    : isInRange
                                    ? 'bg-orange-100 text-orange-600'
                                    : 'text-gray-700'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          }

                          const remainingCells = 42 - days.length;
                          for (let day = 1; day <= remainingCells; day++) {
                            days.push(
                              <button
                                key={`next-${day}`}
                                type="button"
                                className="text-xs text-gray-400 py-1 hover:bg-gray-100 rounded"
                                disabled
                              >
                                {day}
                              </button>
                            );
                          }

                          return days;
                        })()}
                      </div>

                      <div className="mt-4 text-center">
                        <div className="text-sm text-gray-600 mb-3">
                          {customStartDate && customEndDate
                            ? `${formatDate(customStartDate)} - ${formatDate(customEndDate)}`
                            : customStartDate
                            ? `${formatDate(customStartDate)} - Select end date`
                            : 'Select start date'}
                        </div>

                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={handleCancelCustomRange}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleApplyCustomRange}
                            disabled={!customStartDate || !customEndDate}
                            className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col max-w-[180px]">
            <label className="text-xs text-gray-600 mb-1">Supervisor</label>
            <div className="relative supervisor-autocomplete-container">
              <input
                type="text"
                value={supervisorInputValue}
                onChange={handleSupervisorInputChange}
                onFocus={() => setShowSupervisorDropdown(true)}
                placeholder="Search "
                className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                autoComplete="off"
              />
              <div className="absolute inset-y-0 right-0 flex items-center">
                {supervisorInputValue ? (
                  <button
                    type="button"
                    onClick={handleClearSupervisor}
                    className="pr-3 hover:bg-gray-100 rounded-r-md flex items-center"
                    title="Clear supervisor"
                  >
                    <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ) : (
                  <div className="pr-3 pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                )}
              </div>
              {showSupervisorDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                  <div className="py-1">
                    {filteredSupervisorOptions.length > 0 ? (
                      filteredSupervisorOptions.map((opt) => (
                        <button
                          key={String(opt.value)}
                          type="button"
                          onClick={() => handleSupervisorSelect(opt)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-100 focus:bg-orange-100 focus:outline-none ${
                            selectedSupervisor === opt.value
                              ? 'bg-orange-100 text-orange-600 font-medium'
                              : 'text-gray-700'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500 text-center">No matches found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col max-w-[160px]">
            <label className="text-xs text-gray-600 mb-1">Mode of Transaction</label>
            <select
              value={selectedModeOfTranscation}
              onChange={(e) => setSelectedModeOfTranscation(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All</option>
              {modeOfTranscationOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col max-w-[160px]">
            <label className="text-xs text-gray-600 mb-1">Bank</label>
            <select
              value={selectedBankName}
              onChange={(e) => setSelectedBankName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">All</option>
              {bankNameOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!hasActiveFilters}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              title="Reset filters"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>Reset</span>
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Export as PDF"
            >
              <img src="/assets/img/pdf.png" alt="PDF" className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              title="Export as Excel"
            >
              <img src="/assets/img/sheet.png" alt="Excel" className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
     

      {isLoading ? (
        <div className="bg-white shadow rounded-lg p-8 flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600 text-sm">Loading data...</p>
          </div>
        </div>
      ) : (
        <Grid cols={1} className="p-0 flex flex-wrap items-end rounded-lg" style={{marginTop: '10px'}}>
          <div className="">
            <div className="overflow-x-auto">
              <div 
                className={`relative overflow-y-auto ${!showFilters ? 'bg-white' : ''}`}
                style={
                  isAdministrator
                    ? { height: 'calc(100vh - 370px)', backgroundColor: '#fff' }
                    : { height: 'calc(100vh - 270px)', backgroundColor: '#fff' }
                }
              >
                <table className="min-w-full divide-y divide-gray-200 shadow rounded-lg border border-gray-200">
                  <thead className="sticky top-0 z-10" style={{backgroundColor: "rgb(0, 150, 136)"}}>
                    <tr>
                      {visibleColumns.dateOfTranscation && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('dateOfTranscation')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Date of Transaction</span>
                            {getSortIcon('dateOfTranscation')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.siteCode && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('siteCode')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Site Code</span>
                            {getSortIcon('siteCode')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.plant && (
                        <th
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{ backgroundColor: 'rgb(0, 150, 136)' }}
                          onMouseEnter={(e) => { e.target.style.backgroundColor = 'rgb(0, 130, 120)'; }}
                          onMouseLeave={(e) => { e.target.style.backgroundColor = 'rgb(0, 150, 136)'; }}
                          onClick={() => handleSort('plant')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Plant</span>
                            {getSortIcon('plant')}
                          </div>
                        </th>
                      )}
                       {visibleColumns.modeOfTranscation && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Mode of Transaction</th>
                      )}
                       {visibleColumns.chequeNo && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Cheque No</th>
                      )}
                      {visibleColumns.transcationId && (
                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Transaction ID</th>
                      )}
                     
                      {visibleColumns.transcationdetails && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Transaction Details</th>
                      )}
                     
                      {visibleColumns.supervisorName && (
                        <th 
                          className="px-3 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white w-40 max-w-[10rem]"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('supervisorName')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Supervisor Name</span>
                            {getSortIcon('supervisorName')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.amount && (
                        <th 
                          className="px-3 py-3 text-right text-xs font-medium tracking-wider cursor-pointer select-none text-white w-28 max-w-[7rem]"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('amount')}
                        >
                          <div className="flex items-center justify-end space-x-1">
                            <span>Amount</span>
                            {getSortIcon('amount')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.bankName && (
                        <th 
                          className="px-6 py-3 text-left text-xs font-medium tracking-wider cursor-pointer select-none text-white"
                          style={{backgroundColor: "rgb(0, 150, 136)"}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = "rgb(0, 130, 120)"}
                          onMouseLeave={(e) => e.target.style.backgroundColor = "rgb(0, 150, 136)"}
                          onClick={() => handleSort('bankName')}
                        >
                          <div className="flex items-center space-x-1">
                            <span>Bank</span>
                            {getSortIcon('bankName')}
                          </div>
                        </th>
                      )}
                      {visibleColumns.comments && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Comments</th>
                      )}
                      {visibleColumns.actions && (
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-white" style={{backgroundColor: "rgb(0, 150, 136)"}}>Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {!isLoading && paginatedData.length === 0 ? (
                      <tr>
                        <td colSpan={getVisibleColumnCount()} className="px-6 py-8 text-center text-sm text-gray-500">
                          No advance details found.
                        </td>
                      </tr>
                    ) : (
                      paginatedData.map((record) => (
                        <tr key={record.id}>
                          {visibleColumns.dateOfTranscation && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{formatDateMMMDDYYYY(record.dateOfTranscation)}</td>
                          )}
                          {visibleColumns.siteCode && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.siteCode || '-'}</td>
                          )}
                          {visibleColumns.plant && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record.plant || '-'}</td>
                          )}
                           {visibleColumns.modeOfTranscation && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.modeOfTranscation}</td>
                          )}
                           {visibleColumns.chequeNo && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{record.chequeNo}</td>
                          )}
                          {visibleColumns.transcationId && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{record.transcationId}</td>
                          )}
                         
                          {visibleColumns.transcationdetails && (
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                              <div className="truncate" title={record.transcationdetails || '-'}>
                                {record.transcationdetails || '-'}
                              </div>
                            </td>
                          )}
                         
                          {visibleColumns.supervisorName && (
                            <td className="px-3 py-4 text-sm font-medium text-gray-900 max-w-[10rem]">
                              <div className="truncate" title={record.supervisorName || '-'}>
                                {record.supervisorName || '-'}
                              </div>
                            </td>
                          )}
                          {visibleColumns.amount && (
                            <td className="px-3 py-4 whitespace-nowrap text-sm text-right text-gray-900 w-28 max-w-[7rem]">{record.amount}</td>
                          )}
                          {visibleColumns.bankName && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{record.bankName || '-'}</td>
                          )}
                          {visibleColumns.comments && (
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                              <div className="truncate" title={record.comments || '-'}>
                                {record.comments || '-'}
                              </div>
                            </td>
                          )}
                          {visibleColumns.actions && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEdit(record.id)}
                                  className="text-indigo-600 hover:text-indigo-900 focus:outline-none focus:underline"
                                  title="Edit"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => handleDelete(record.advanceId)}
                                  className="text-red-600 hover:text-red-900 focus:outline-none focus:underline"
                                  title="Delete"
                                >
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredData.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </Grid>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 m-0 overflow-hidden" style={{marginTop: 0}}>
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleClose}
            ></div>

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left shadow-xl transform transition-all modal-card app-modal-panel">
              <form onSubmit={handleSubmit} noValidate className="flex h-full min-h-0 flex-col">
                {/* Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {isEditMode ? 'Edit Advance Detail' : 'Add Advance Detail'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Form content */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-4">
                  {/* 1. Location & Date */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date of Transaction <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="dateOfTranscation"
                          value={formData.dateOfTranscation}
                          onChange={handleInputChange}
                          max={getTodayDate()}
                          className={modalFieldClass('dateOfTranscation')}
                        />
                        {formErrors.dateOfTranscation && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.dateOfTranscation}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Site Code
                        </label>
                        <select
                          name="siteCode"
                          value={formData.siteCode}
                          onChange={handleInputChange}
                          className={modalFieldClass('siteCode')}
                        >
                          <option value="">Select Site Code</option>
                          {siteOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Plant
                        </label>
                        <select
                          name="plant"
                          value={formData.plant}
                          onChange={handleInputChange}
                          className={modalFieldClass('plant')}
                        >
                          <option value="">Select Plant</option>
                          {plantSelectOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </ModalSection>

                  {/* 2. Reference Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mode of Transaction <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="modeOfTranscation"
                          value={formData.modeOfTranscation}
                          onChange={handleInputChange}
                          className={modalFieldClass('modeOfTranscation')}
                        >
                          <option value="">Select</option>
                          <option value="Cash">Cash</option>
                          <option value="Cheque">Cheque</option>
                          <option value="UPI">UPI</option>
                          <option value="onlineBank">Online Bank</option>
                        </select>
                        {formErrors.modeOfTranscation && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.modeOfTranscation}</p>
                        )}
                      </div>

                      {(formData.modeOfTranscation === 'Cheque' ||
                        formData.modeOfTranscation === 'Bank' ||
                        formData.modeOfTranscation === 'Credit' ||
                        formData.modeOfTranscation === 'UPI' ||
                        formData.modeOfTranscation === 'onlineBank') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {formData.modeOfTranscation === 'Cheque' ? 'Cheque No' : 'Transaction ID'}
                          </label>
                          <input
                            type="text"
                            name="transcationId"
                            value={formData.transcationId}
                            onChange={handleInputChange}
                            className={modalFieldClass('transcationId')}
                            placeholder=" "
                          />
                          {formErrors.transcationId && (
                            <p className="mt-1 text-xs text-red-600">{formErrors.transcationId}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Transaction Details
                        </label>
                        <input
                          type="text"
                          name="transcationdetails"
                          value={formData.transcationdetails}
                          onChange={handleInputChange}
                          className={modalFieldClass('transcationdetails')}
                          placeholder=""
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Source <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="fromBankAccount"
                          value={formData.fromBankAccount}
                          onChange={handleInputChange}
                          className={modalFieldClass('fromBankAccount')}
                        >
                          <option value="">Select</option>
                          <option value="TKD">TKD</option>
                          <option value="DKD">DKD</option>
                        </select>
                        {formErrors.fromBankAccount && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.fromBankAccount}</p>
                        )}
                      </div>
                    </div>
                  </ModalSection>

                  {/* 3. Amount & Approval */}
                  <ModalSection>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Amount <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          name="amount"
                          value={formData.amount}
                          onChange={handleInputChange}
                          autoComplete="off"
                          className={modalFieldClass('amount')}
                          placeholder=" "
                        />
                        {formErrors.amount && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.amount}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Supervisor Name <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="supervisorName"
                          value={formData.supervisorName}
                          onChange={handleInputChange}
                          className={modalFieldClass('supervisorName')}
                        >
                          <option value="">Select Supervisor</option>
                          {supervisorsFromApi.length > 0
                            ? supervisorsFromApi.map((sup) => (
                                <option key={sup.value} value={sup.value}>
                                  {sup.label}
                                </option>
                              ))
                            : advanceDetails
                                .map((item) => item.supervisorName)
                                .filter((name, idx, arr) => name && arr.indexOf(name) === idx)
                                .map((name) => (
                                  <option key={name} value={name}>
                                    {name}
                                  </option>
                                ))}
                        </select>
                        {formErrors.supervisorName && (
                          <p className="mt-1 text-xs text-red-600">{formErrors.supervisorName}</p>
                        )}
                      </div>
                    </div>
                  </ModalSection>

                  {/* 4. Additional Details */}
                  <ModalSection>
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Comments
                        </label>
                        <textarea
                          name="comments"
                          value={formData.comments}
                          onChange={handleInputChange}
                          rows={3}
                          className={modalFieldClass('comments', 'resize-vertical')}
                          placeholder=""
                        />
                      </div>
                    </div>
                  </ModalSection>
                </div>
                 
                {/* Footer */}
                <div className="flex flex-shrink-0 justify-end space-x-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    style={{ backgroundColor: '#009688' }}
                  >
                    {isEditMode ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Background overlay */}
          <div 
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={handleCancelDelete}
          ></div>

          {/* Confirmation Dialog */}
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
            <div className="p-6">
              {/* Warning Icon */}
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
                <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              
              {/* Message */}
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Confirm Delete
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Are you sure you want to delete this record? This action cannot be undone.
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-2 justify-center">
                <button
                  type="button"
                  onClick={handleCancelDelete}
                  className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDelete}
                  className="px-6 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:opacity-90"
                  style={{ backgroundColor: '#dc2626' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AdvanceDetails;

